import PropTypes from 'prop-types';
import React from 'react';

import * as S from './Input.styles';

export const InputAppearance = {
  PRIMARY: 'primary',
  OUTLINE: 'outline',
};

const getContent = ({ ...commonProps }) => {
  const { value, placeholder, icon, type } = commonProps;

  if (type === 'textarea') {
    return <S.TextAreaField {...commonProps} value={value ?? placeholder} />;
  }

  return !icon ? (
    <S.InputField {...commonProps} value={value ?? placeholder} />
  ) : (
    <>
      {icon}
      <S.InputField icon={true} {...commonProps} value={value ?? placeholder} />
    </>
  );
};

export const Input = ({
  hasError,
  label,
  disabled,
  isRequired,
  icon,
  appearance,
  hasFocus,
  placeholder,
  value,
  onChange,
  onKeyDown,
  type,
}) => {
  if (!onChange) {
    return null;
  }

  const content = getContent({
    icon,
    appearance,
    hasError,
    hasFocus,
    placeholder,
    disabled,
    value,
    onChange,
    onKeyDown,
    type,
  });

  const inputDescription = `${label}${isRequired ? ' *' : ''}`;

  return (
    <S.InputContainer>
      {label && (
        <S.StyledLabel hasError={hasError}>{inputDescription}</S.StyledLabel>
      )}
      <S.StyledInput>{content}</S.StyledInput>
    </S.InputContainer>
  );
};

Input.propTypes = {
  /**
   * Set the appareance of the input.
   */
  appearance: PropTypes.oneOf(Object.values(InputAppearance)),
  /**
   * Disable the input.
   */
  disabled: PropTypes.bool,
  /**
   * Display the label in red if true.
   */
  hasError: PropTypes.bool,
  /**
   * Display a border when this input is focus.
   * Has effect only if appearance === primary.
   */
  hasFocus: PropTypes.bool,
  /**
   * Optional content.
   */
  icon: PropTypes.node,
  /**
   * Define if the input field is required or not
   * and add a '*' if true.
   */
  isRequired: PropTypes.bool,
  /**
   * Description of the input field.
   */
  label: PropTypes.string,
  /**
   * Function called every time the event is triggered.
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Function triggered when the user types a specific key
   * while using the input.
   */
  onKeyDown: PropTypes.func,
  /**
   * Defines the input placeholder.
   */
  placeholder: PropTypes.string,
  /**
   * Input type (text, email, password...)
   */
  type: PropTypes.string,
  /**
   * Value of the input element.
   */
  value: PropTypes.string.isRequired,
};

Input.defaultProps = {
  appearance: InputAppearance.OUTLINE,
  isRequired: false,
  hasError: false,
  hasFocus: true,
  type: 'text',
};
